// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Ben Richardson <hi@ben.gy>
//
// "Who holds it" — the link between the 683 determinations on the National
// Native Title Register and the 296 corporations on the RNTBC areas register.
//
// THE FORM IS ANCHORED BY CONSTRUCTION. There is no force simulation and no
// animation frame anywhere in this file: every angle, radius and control point
// is closed-form trigonometry, computed once, before the SVG is ever appended.
// The diagram is motionless from the first frame and identical on every load.
//
// THE ABSENCE IS THE FINDING. A determination with no corporation linked to it
// gets a short, hollow tick with no stalk running down into the hub — and the
// count of those ticks, and what they turn out to be, is computed here from the
// data rather than assumed. Every one of the determinations that found native
// title does not exist is un-spoked, which is what the law would predict: a
// finding that native title does not exist over an area leaves nothing for a
// body corporate to hold.
//
// NOTHING ON THIS VIEW IS ORDERED BY HOW MUCH COUNTRY A GROUP HOLDS. A
// corporation's dot is sized by the NUMBER of determinations it is linked to,
// which is an administrative fact about the corporation, and the corporation
// list underneath is alphabetical.

import { FINDING, FINDING_ORDER, METHOD, type Finding } from '../theme';
import { el, chart } from '../components/svg';
import { esc, num, km2, longDate, tip, tipAttr, plural } from '../format';
import { openDetermination, indexHolders } from '../components/detail';
import { glossTerm, glossIcon } from '../components/glossary';
import { openOverlay, closeButton } from '../overlay';
import { attachSvgZoom } from '../utils/svgZoom';
import { getMeta, getDeterminations, loadHolders } from '../data';
import type { Determination, Holder } from '../types';

/* ─────────────────────────── geometry constants ──────────────────────────── */

const SIZE = 900;
const CX = SIZE / 2;
const CY = SIZE / 2;
/** The rim: where the determination ticks begin. Everything scales off this. */
const R = 320;
const TICK_LEN = 17;
const TICK_LEN_UNSPOKED = 10;
const ARC_R = R + TICK_LEN + 9;
const LABEL_R = ARC_R + 13;
/** The inner ring of corporations, at about 0.62r, spread over four lanes. */
const LANES = [0.56, 0.6, 0.64, 0.68].map((k) => k * R);
/** The bundling hub: control points sit this far out on the angular bisector. */
const CTRL_R = 0.18 * R;

const TAU = Math.PI * 2;
const GAP = (3.2 * Math.PI) / 180;
const ZERO_SLOT = (7 * Math.PI) / 180;
const START = -Math.PI / 2;

/** The conventional administrative order of the states and territories. */
const JUR_ORDER = ['NSW', 'VIC', 'QLD', 'SA', 'WA', 'TAS', 'NT', 'ACT'];
const JUR_NAME: Record<string, string> = {
  NSW: 'New South Wales',
  VIC: 'Victoria',
  QLD: 'Queensland',
  SA: 'South Australia',
  WA: 'Western Australia',
  TAS: 'Tasmania',
  NT: 'Northern Territory',
  ACT: 'Australian Capital Territory',
};

const jurName = (code: string) => JUR_NAME[code] ?? code;

function polar(angle: number, radius: number): [number, number] {
  return [CX + Math.cos(angle) * radius, CY + Math.sin(angle) * radius];
}

function degrees(rad: number): number {
  let d = (rad * 180) / Math.PI;
  while (d < -180) d += 360;
  while (d >= 180) d -= 360;
  return d;
}

/* ───────────────────────────── internal shapes ───────────────────────────── */

interface Sector {
  code: string;
  count: number;
  a0: number;
  a1: number;
  dets: Determination[];
}

interface Corp {
  key: string;
  holder: Holder;
  dets: Determination[];
  angle: number;
  radius: number;
  dotR: number;
  jurisdictions: string[];
  cross: boolean;
}

/* ────────────────────────────────── view ─────────────────────────────────── */

export async function renderHolders(host: HTMLElement): Promise<void> {
  const holders = await loadHolders();
  indexHolders(holders);

  const meta = getMeta();
  const dets = getDeterminations();
  const byId = new Map(dets.map((d) => [d.id, d]));

  // ── the link structure, computed once ────────────────────────────────────
  const holdersByDet = new Map<string, Holder[]>();
  let resolvedLinks = 0;
  let unresolvedLinks = 0;
  for (const h of holders) {
    for (const id of h.determinations) {
      if (!byId.has(id)) { unresolvedLinks += 1; continue; }
      resolvedLinks += 1;
      if (!holdersByDet.has(id)) holdersByDet.set(id, []);
      holdersByDet.get(id)!.push(h);
    }
  }

  const spokedIds = new Set(holdersByDet.keys());
  const unspoked = dets.filter((d) => !spokedIds.has(d.id));
  const unspokedByFinding = new Map<Finding | 'unrecorded', number>();
  for (const d of unspoked) {
    const k = (d.finding ?? 'unrecorded') as Finding | 'unrecorded';
    unspokedByFinding.set(k, (unspokedByFinding.get(k) ?? 0) + 1);
  }
  const noneTotal = dets.filter((d) => d.finding === 'none').length;
  const unspokedNone = unspokedByFinding.get('none') ?? 0;
  const unspokedRecognising = unspoked.length - unspokedNone;
  const unspokedNamingCorp = unspoked.filter((d) => d.finding !== 'none' && d.bodyCorporate).length;

  // ── the rim: one sector per jurisdiction, ticks ordered by date ───────────
  const codes = new Set<string>([...dets.map((d) => d.juris), ...Object.keys(meta.landAreaKm2)]);
  const ordered = [
    ...JUR_ORDER.filter((c) => codes.has(c)),
    ...[...codes].filter((c) => !JUR_ORDER.includes(c)).sort(),
  ];
  const sectors: Sector[] = ordered.map((code) => {
    const own = dets
      .filter((d) => d.juris === code)
      .sort((a, b) => (a.date ?? '').localeCompare(b.date ?? '') || a.id.localeCompare(b.id));
    return { code, count: own.length, a0: 0, a1: 0, dets: own };
  });

  const zeroSectors = sectors.filter((s) => s.count === 0);
  const totalDets = sectors.reduce((a, s) => a + s.count, 0);
  const available = TAU - sectors.length * GAP - zeroSectors.length * ZERO_SLOT;
  let cursor = START + GAP / 2;
  for (const s of sectors) {
    const span = s.count > 0 ? (available * s.count) / totalDets : ZERO_SLOT;
    s.a0 = cursor;
    s.a1 = cursor + span;
    cursor = s.a1 + GAP;
  }

  const angleOf = new Map<string, number>();
  for (const s of sectors) {
    if (!s.count) continue;
    const step = (s.a1 - s.a0) / s.count;
    s.dets.forEach((d, i) => angleOf.set(d.id, s.a0 + (i + 0.5) * step));
  }

  // ── the inner ring: circular mean angle, then a deterministic lane ────────
  const corps: Corp[] = [];
  const unplaced: Holder[] = [];
  holders.forEach((h, i) => {
    const own = h.determinations
      .map((id) => byId.get(id))
      .filter((d): d is Determination => Boolean(d))
      .sort((a, b) => (a.date ?? '').localeCompare(b.date ?? ''));
    if (!own.length) { unplaced.push(h); return; }
    // The circular mean, via atan2 of the summed unit vectors. A plain
    // arithmetic mean of angles is wrong across the 0/360 boundary — it would
    // put a corporation holding country at 350° and 10° at 180°, on the far
    // side of the continent from both.
    let sx = 0;
    let sy = 0;
    for (const d of own) {
      const a = angleOf.get(d.id)!;
      sx += Math.cos(a);
      sy += Math.sin(a);
    }
    const angle = Math.hypot(sx, sy) < 1e-9 ? angleOf.get(own[0].id)! : Math.atan2(sy, sx);
    const jurisdictions = [...new Set(own.map((d) => d.juris))]
      .sort((a, b) => JUR_ORDER.indexOf(a) - JUR_ORDER.indexOf(b));
    corps.push({
      key: `c${i}`,
      holder: h,
      dets: own,
      angle,
      radius: LANES[1],
      dotR: 3 * Math.sqrt(own.length),
      jurisdictions,
      cross: jurisdictions.length > 1,
    });
  });

  // Four closely-spaced lanes, assigned greedily in angle order, purely so that
  // corporations whose country sits in the same part of the rim stay separately
  // visible and separately hoverable. The lane carries NO meaning.
  const laneOrder = [...corps].sort((a, b) => a.angle - b.angle);
  const lastInLane: ({ angle: number; dotR: number } | null)[] = LANES.map(() => null);
  for (const c of laneOrder) {
    let chosen = 0;
    let bestSlack = -Infinity;
    for (let l = 0; l < LANES.length; l += 1) {
      const prev = lastInLane[l];
      if (!prev) { chosen = l; bestSlack = Infinity; break; }
      const chord = 2 * LANES[l] * Math.abs(Math.sin((c.angle - prev.angle) / 2));
      const slack = chord - (prev.dotR + c.dotR + 2.5);
      if (slack >= 0) { chosen = l; bestSlack = slack; break; }
      if (slack > bestSlack) { bestSlack = slack; chosen = l; }
    }
    c.radius = LANES[chosen];
    lastInLane[chosen] = { angle: c.angle, dotR: c.dotR };
  }

  const corpsByDet = new Map<string, Corp[]>();
  for (const c of corps) {
    for (const d of c.dets) {
      if (!corpsByDet.has(d.id)) corpsByDet.set(d.id, []);
      corpsByDet.get(d.id)!.push(c);
    }
  }

  const crossCorps = corps.filter((c) => c.cross);
  const meanLinks = corps.length ? resolvedLinks / corps.length : 0;

  /* ───────────────────────────── the page ──────────────────────────────── */

  const wrap = document.createElement('div');
  wrap.className = 'stack';
  wrap.innerHTML = `
    <div class="view-head">
      <h1>Who holds it</h1>
      <p class="lede">When a court recognises native title, the holders must nominate a corporation to
        hold or manage it for them — a ${glossTerm('rntbc', 'registered native title body corporate')},
        usually called a PBC. This is every one of those corporations, and every determination each of
        them is linked to.</p>
      <p>The outer rim is the ${esc(num(dets.length))} determinations, one tick each, grouped by state or
        territory and running in date order within each group, so the rim doubles as a clock. The inner
        ring is the ${esc(num(corps.length))} corporations, each sitting at the average direction of the
        country it is linked to. A curve is one link between the two.</p>
    </div>

    <div class="stat-row">
      <div class="stat" data-tip="${tipAttr(
        `${num(meta.counts.bodiesCorporate)} corporations on the RNTBC areas register`,
        `${num(corps.length)} of them are linked to at least one determination`,
        unplaced.length ? `${plural(unplaced.length, 'corporation is', 'corporations are')} on the register with no determination linked` : '',
      )}">
        <div class="stat-value">${esc(num(holders.length))}</div>
        <div class="stat-label">Corporations on the register</div>
        <div class="stat-note">${esc(num(corps.length))} of them linked to at least one determination</div>
      </div>
      <div class="stat" data-tip="${tipAttr(
        `${num(resolvedLinks)} corporation-to-determination links`,
        `across ${num(corps.length)} corporations — a mean of ${meanLinks.toFixed(2)} determinations each`,
        `the most any one corporation is linked to is ${num(corps.reduce((m, c) => Math.max(m, c.dets.length), 0))}`,
      )}">
        <div class="stat-value">${esc(num(resolvedLinks))}</div>
        <div class="stat-label">Links between the two registers</div>
        <div class="stat-note">a mean of ${esc(meanLinks.toFixed(2))} determinations per corporation</div>
      </div>
      <div class="stat" data-tip="${tipAttr(
        `${num(spokedIds.size)} of ${num(dets.length)} determinations have at least one corporation linked to them`,
        `${num(dets.length - spokedIds.size)} have none`,
      )}">
        <div class="stat-value">${esc(num(spokedIds.size))}</div>
        <div class="stat-label">Determinations with a holding corporation</div>
        <div class="stat-note">of ${esc(num(dets.length))} on the register</div>
      </div>
      <div class="stat" data-tip="${tipAttr(
        `${num(unspoked.length)} determinations have no corporation linked to them`,
        `${num(unspokedNone)} of those found that native title does not exist`,
      )}">
        <div class="stat-value">${esc(num(unspoked.length))}</div>
        <div class="stat-label">Determinations with none</div>
        <div class="stat-note">drawn as short hollow ticks with no stalk</div>
      </div>
    </div>

    <div class="card stack-sm">
      <div class="card-title">What a body corporate actually is${glossIcon('rntbc')}</div>
      <p class="small">A determination that recognises native title does not hand anyone a title deed. It
        recognises that a group already holds rights under their own law, and then the Native Title Act
        requires that group to nominate a corporation — a prescribed body corporate, registered with the
        Office of the Registrar of Indigenous Corporations — to hold that native title on trust or to
        manage it as agent for the holders. Once the determination takes effect the corporation goes on
        the Tribunal's register as a <em>registered</em> native title body corporate, and from then on it
        is the entity that governments, miners, pastoralists and councils must deal with about that
        country.</p>
      <p class="small muted">So the corporation is an administrative requirement of Australian law, not a
        traditional institution. It is the legal doorway to country, and the two registers joined here —
        the determinations register and the RNTBC areas register — are the public record of which doorway
        belongs to which decision.</p>
    </div>
  `;

  /* ──────────────────────────── the diagram ───────────────────────────── */

  const controls = document.createElement('div');
  controls.className = 'chips';

  const crossBtn = document.createElement('button');
  crossBtn.type = 'button';
  crossBtn.className = 'btn btn-sm';
  crossBtn.setAttribute('aria-pressed', 'false');
  crossBtn.textContent = crossCorps.length === 1
    ? 'Show only the corporation holding across a state border'
    : `Show only the ${num(crossCorps.length)} corporations holding across a state border`;
  crossBtn.setAttribute('data-tip', tip(
    'Almost every corporation on this ring is linked to determinations inside one state or territory.',
    `${plural(crossCorps.length, 'corporation is', 'corporations are')} linked across a border, and only those curves cross the hub.`,
  ));

  const resetBtn = document.createElement('button');
  resetBtn.type = 'button';
  resetBtn.className = 'btn btn-sm';
  resetBtn.textContent = 'Reset the view';
  resetBtn.setAttribute('data-tip', tip('Draw every link again and reset the zoom.'));

  controls.append(crossBtn, resetBtn);

  const frame = document.createElement('div');
  frame.className = 'chart-frame holders-frame';

  const svg = chart(SIZE, SIZE, `Every link between ${num(dets.length)} native title determinations on the outer rim and ${num(corps.length)} holding corporations on the inner ring`);
  svg.classList.add('holders-graph');

  const gSectors = el('g');
  const gEdges = el('g');
  const gCrossEdges = el('g');
  const gTicks = el('g');
  const gDots = el('g');
  const gLabels = el('g');
  svg.append(gSectors, gEdges, gCrossEdges, gTicks, gDots, gLabels);

  // sector arcs + labels (including the real zeros) ------------------------
  for (const s of sectors) {
    const [x0, y0] = polar(s.a0, ARC_R);
    const [x1, y1] = polar(s.a1, ARC_R);
    const large = s.a1 - s.a0 > Math.PI ? 1 : 0;
    const zero = s.count === 0;

    const g = el('g', { class: 'holders-sector-hit', tabindex: '0', role: 'button',
      'aria-label': `${jurName(s.code)}: ${s.count} determinations. Open the summary.` });
    g.setAttribute('data-h-kind', 'sector');
    g.setAttribute('data-h-key', s.code);
    g.setAttribute('data-tip', tip(
      jurName(s.code),
      zero
        ? 'Zero determinations. That is a real zero, not missing data.'
        : `${plural(s.count, 'determination')} · ${plural(new Set(s.dets.flatMap((d) => corpsByDet.get(d.id) ?? []).map((c) => c.key)).size, 'holding corporation')}`,
      'Click for the summary',
    ));

    // A transparent fat stroke so the thin arc has a real hit area.
    g.append(el('path', {
      d: `M ${x0.toFixed(2)} ${y0.toFixed(2)} A ${ARC_R} ${ARC_R} 0 ${large} 1 ${x1.toFixed(2)} ${y1.toFixed(2)}`,
      fill: 'none', stroke: 'transparent', 'stroke-width': 28,
    }));
    g.append(el('path', {
      class: `holders-sector-arc${zero ? ' is-zero' : ''}`,
      d: `M ${x0.toFixed(2)} ${y0.toFixed(2)} A ${ARC_R} ${ARC_R} 0 ${large} 1 ${x1.toFixed(2)} ${y1.toFixed(2)}`,
    }));

    const mid = (s.a0 + s.a1) / 2;
    const [lx, ly] = polar(mid, LABEL_R);
    const dm = degrees(mid);
    const flip = dm > 90 || dm < -90;
    const label = el('text', {
      class: `holders-sector-label${zero ? ' is-zero' : ''}`,
      x: lx.toFixed(2), y: ly.toFixed(2),
      transform: `rotate(${(flip ? dm + 180 : dm).toFixed(2)} ${lx.toFixed(2)} ${ly.toFixed(2)})`,
      'text-anchor': flip ? 'end' : 'start',
      'dominant-baseline': 'middle',
    }, `${s.code} ${s.count}`);
    g.append(label);
    gSectors.append(g);
  }

  // edges ------------------------------------------------------------------
  const edgesByDet = new Map<string, SVGElement[]>();
  const edgesByCorp = new Map<string, SVGElement[]>();
  const pushEdge = (map: Map<string, SVGElement[]>, key: string, node: SVGElement) => {
    if (!map.has(key)) map.set(key, []);
    map.get(key)!.push(node);
  };

  for (const c of corps) {
    const [x0, y0] = polar(c.angle, c.radius);
    for (const d of c.dets) {
      const a = angleOf.get(d.id)!;
      const [x1, y1] = polar(a, R);
      // The control point sits near the centre on the ANGULAR BISECTOR of the
      // two endpoints, so links bundle through the hub instead of crossing the
      // ring as a haystack of straight chords.
      const bx = Math.cos(c.angle) + Math.cos(a);
      const by = Math.sin(c.angle) + Math.sin(a);
      const bis = Math.hypot(bx, by) < 1e-9 ? c.angle : Math.atan2(by, bx);
      const [cx, cy] = polar(bis, CTRL_R);
      const stroke = d.finding ? FINDING[d.finding].fill : 'var(--text-muted)';
      const node = el('path', {
        class: `holders-el holders-edge${c.cross ? ' holders-cross' : ''}`,
        d: `M ${x0.toFixed(2)} ${y0.toFixed(2)} Q ${cx.toFixed(2)} ${cy.toFixed(2)} ${x1.toFixed(2)} ${y1.toFixed(2)}`,
        stroke,
        'stroke-width': c.cross ? 2.1 : 0.85,
        'stroke-linecap': 'round',
      });
      (c.cross ? gCrossEdges : gEdges).append(node);
      pushEdge(edgesByDet, d.id, node);
      pushEdge(edgesByCorp, c.key, node);
    }
  }

  // rim ticks --------------------------------------------------------------
  const tickPitch = (available / totalDets) * R;
  const tickW = Math.max(1.5, Math.min(3, tickPitch * 0.78));
  const tickEl = new Map<string, SVGElement>();

  for (const s of sectors) {
    for (const d of s.dets) {
      const a = angleOf.get(d.id)!;
      const held = corpsByDet.get(d.id) ?? [];
      const spoked = held.length > 0;
      const hue = d.finding ? FINDING[d.finding].fill : 'var(--text-muted)';
      const len = spoked ? TICK_LEN : TICK_LEN_UNSPOKED;
      const x = R + (spoked ? 0 : TICK_LEN - TICK_LEN_UNSPOKED);
      const rect = el('rect', {
        class: `holders-el holders-tick${spoked ? '' : ' holders-unspoked'}`,
        x: (CX + x).toFixed(2),
        y: (CY - tickW / 2).toFixed(2),
        width: len,
        height: tickW,
        transform: `rotate(${degrees(a).toFixed(3)} ${CX} ${CY})`,
        fill: spoked ? hue : 'var(--bg-base)',
        stroke: spoked ? 'none' : hue,
        'stroke-width': spoked ? 0 : 0.75,
      });
      rect.setAttribute('data-h-kind', 'det');
      rect.setAttribute('data-h-key', d.id);
      rect.setAttribute('data-tip', tip(
        d.name ?? d.id,
        `${jurName(d.juris)} · determined ${longDate(d.date)}`,
        `What the court found: ${d.finding ? FINDING[d.finding].register : (d.findingText ?? 'not recorded')}`,
        `How it was decided: ${d.method ? METHOD[d.method].label : 'not recorded'}`,
        `Determination area: ${km2(d.areaSqKm, { exact: true })}${d.sea ? ' (includes sea country)' : ''}`,
        spoked
          ? `Held by: ${held.map((c) => c.holder.name).join(' · ')}`
          : 'No holding corporation is linked to this determination on the RNTBC areas register.',
        'Click for the full record, including the holders text in full',
      ));
      gTicks.append(rect);
      tickEl.set(d.id, rect);
    }
  }

  // corporation dots -------------------------------------------------------
  const dotEl = new Map<string, SVGElement>();
  for (const c of corps) {
    const [x, y] = polar(c.angle, c.radius);
    const dot = el('circle', {
      class: `holders-el holders-dot${c.cross ? ' holders-cross' : ''}`,
      cx: x.toFixed(2), cy: y.toFixed(2), r: c.dotR.toFixed(2),
      fill: 'var(--text-secondary)',
      stroke: c.cross ? 'var(--text-primary)' : 'var(--bg-base)',
      'stroke-width': c.cross ? 1.8 : 0.9,
    });
    dot.setAttribute('data-h-kind', 'corp');
    dot.setAttribute('data-h-key', c.key);
    dot.setAttribute('data-tip', tip(
      c.holder.name,
      `Linked to ${plural(c.dets.length, 'determination')}`,
      c.jurisdictions.map(jurName).join(' and '),
      c.cross ? 'Holds across a state or territory border' : '',
      'Click for every determination it holds',
    ));
    gDots.append(dot);
    dotEl.set(c.key, dot);
  }

  // a quiet caption inside the frame, so the rim reads as a clock -----------
  gLabels.append(el('text', {
    class: 'holders-hub-note', x: CX, y: CY - 8, 'text-anchor': 'middle',
  }, 'Each curve is one link'));
  gLabels.append(el('text', {
    class: 'holders-hub-note', x: CX, y: CY + 12, 'text-anchor': 'middle',
  }, 'between a determination and a corporation'));

  frame.append(svg);

  /* ─────────────────────────── interaction ────────────────────────────── */

  let lit: SVGElement[] = [];
  const clear = () => {
    for (const n of lit) n.classList.remove('holders-on');
    lit = [];
    svg.classList.remove('is-focused');
  };
  const light = (nodes: (SVGElement | undefined)[]) => {
    clear();
    lit = nodes.filter((n): n is SVGElement => Boolean(n));
    for (const n of lit) n.classList.add('holders-on');
    if (lit.length) svg.classList.add('is-focused');
  };

  const focusDet = (id: string) => {
    const held = corpsByDet.get(id) ?? [];
    light([
      tickEl.get(id),
      ...(edgesByDet.get(id) ?? []),
      ...held.map((c) => dotEl.get(c.key)),
    ]);
  };
  const focusCorp = (key: string) => {
    const c = corps.find((x) => x.key === key);
    if (!c) return;
    light([
      dotEl.get(key),
      ...(edgesByCorp.get(key) ?? []),
      ...c.dets.map((d) => tickEl.get(d.id)),
    ]);
  };

  svg.addEventListener('mouseover', (e) => {
    const t = (e.target as Element).closest?.('[data-h-key]');
    if (!t) { clear(); return; }
    const kind = t.getAttribute('data-h-kind');
    const key = t.getAttribute('data-h-key') ?? '';
    if (kind === 'det') focusDet(key);
    else if (kind === 'corp') focusCorp(key);
    else clear();
  });
  svg.addEventListener('mouseleave', clear);

  const activate = (t: Element) => {
    const kind = t.getAttribute('data-h-kind');
    const key = t.getAttribute('data-h-key') ?? '';
    if (kind === 'det') {
      const d = byId.get(key);
      if (d) openDetermination(d);
    } else if (kind === 'corp') {
      const c = corps.find((x) => x.key === key);
      if (c) openCorporation(c, corpsByDet);
    } else if (kind === 'sector') {
      const s = sectors.find((x) => x.code === key);
      if (s) openSector(s, corpsByDet, meta.landAreaKm2[key] ?? null, dets.length);
    }
  };

  svg.addEventListener('click', (e) => {
    const t = (e.target as Element).closest?.('[data-h-key]');
    if (t) activate(t);
  });
  svg.addEventListener('keydown', (e) => {
    if (e.key !== 'Enter' && e.key !== ' ') return;
    const t = (e.target as Element).closest?.('[data-h-key]');
    if (!t) return;
    e.preventDefault();
    activate(t);
  });

  crossBtn.addEventListener('click', () => {
    const next = crossBtn.getAttribute('aria-pressed') !== 'true';
    crossBtn.setAttribute('aria-pressed', String(next));
    svg.classList.toggle('is-cross-only', next);
    clear();
  });
  resetBtn.addEventListener('click', () => {
    crossBtn.setAttribute('aria-pressed', 'false');
    svg.classList.remove('is-cross-only');
    clear();
    zoom.reset();
  });

  /* ─────────────────────────── legend & captions ──────────────────────── */

  const legend = document.createElement('div');
  legend.className = 'legend holders-legend';
  legend.innerHTML = `
    ${FINDING_ORDER.map((f) => `<span class="legend-item" data-tip="${tipAttr(
      FINDING[f].register,
      FINDING[f].plain,
      `${plural(dets.filter((d) => d.finding === f).length, 'determination')} · ${num(unspokedByFinding.get(f) ?? 0)} with no corporation linked`,
    )}"><span class="legend-swatch" style="background:${FINDING[f].fill}"></span>
      <span>${esc(FINDING[f].label)}</span></span>`).join('')}
    <span class="legend-item" data-tip="${tipAttr(
      'A determination with no corporation linked to it on the RNTBC areas register.',
      'Drawn short and hollow, with no stalk running down into the hub.',
      `${plural(unspoked.length, 'determination')}`,
    )}"><span class="legend-swatch holders-swatch-hollow"></span>
      <span>No corporation linked</span></span>
    <span class="legend-item" data-tip="${tipAttr(
      'One corporation. The dot area is the square root of the NUMBER of determinations it is linked to.',
      'Nothing on this view is sized by area of country.',
    )}"><span class="legend-swatch holders-swatch-dot"></span>
      <span>A corporation</span></span>
    <span class="legend-item" data-tip="${tipAttr(
      `${plural(crossCorps.length, 'corporation')} ${crossCorps.length === 1 ? 'is' : 'are'} linked to determinations in more than one state or territory.`,
      'Their links are the only ones that cross the hub, and they are stroked heavier and drawn last.',
    )}"><span class="legend-swatch holders-swatch-cross"></span>
      <span>Links crossing a border</span></span>
  `;

  const absence = document.createElement('div');
  absence.className = 'note caveat';
  absence.hidden = unspoked.length === 0;
  absence.innerHTML = `<strong>${esc(num(unspoked.length))} of the ${esc(num(dets.length))} determinations
    have no holding corporation, and that is mostly not a gap in the record.</strong>
    <p class="small">${esc(num(unspokedNone))} of them — every single one of the
    ${esc(num(noneTotal))} determinations on the register that found
    ${glossTerm('does-not-exist', 'native title does not exist')} — needs no body corporate at all: there
    is no recognised native title for anyone to hold, so the Act requires nobody to be nominated. That
    accounts for ${esc(((unspokedNone / Math.max(1, unspoked.length)) * 100).toFixed(1))}% of the hollow
    ticks.</p>
    <p class="small">The other ${esc(num(unspokedRecognising))} did recognise native title, or decided
    compensation, and ${esc(num(unspokedNamingCorp))} of those ${esc(unspokedNamingCorp === 1 ? 'names' : 'name')}
    a body corporate in the determination's own record — the corporation exists, but the RNTBC areas
    register does not carry a link back to that determination. Those are register-linkage absences, not
    country without a corporation, and this view does not treat them as the same thing.</p>`;

  const crossNote = document.createElement('div');
  crossNote.className = 'note caveat';
  crossNote.hidden = crossCorps.length === 0;
  crossNote.innerHTML = `<strong>Almost nothing crosses a border.</strong>
    <p class="small">Of ${esc(num(corps.length))} corporations, ${esc(num(crossCorps.length))} are linked
    to determinations in more than one state or territory:
    ${crossCorps.map((c) => `<span class="verbatim holders-inline-name">${esc(c.holder.name)}</span>
      <span class="xsmall muted">(${esc(c.jurisdictions.map(jurName).join(' and '))})</span>`).join('; ')}.
    Country does not stop at a state border, but the registers do: each determination is made under one
    state or territory's legal regime, and the corporation is nominated determination by determination.
    Everything else on this ring bundles inside a single sector.</p>`;

  const zeroNote = document.createElement('div');
  zeroNote.className = 'note caveat';
  zeroNote.hidden = zeroSectors.length === 0;
  zeroNote.innerHTML = `<strong>${esc(plural(zeroSectors.length, 'sector'))} on the rim ${
    zeroSectors.length === 1 ? 'holds' : 'hold'} nothing.</strong>
    <p class="small">${esc(zeroSectors.map((s) => jurName(s.code)).join(' and '))} ${
      zeroSectors.length === 1 ? 'has' : 'have'} zero determinations on the National Native Title Register,
    so ${zeroSectors.length === 1 ? 'it has' : 'they have'} zero corporations here. They are drawn as
    labelled, dashed, empty sectors rather than left out: a real zero is a finding about the register, and a
    jurisdiction that is simply missing from a chart reads as an oversight instead. Click ${
      zeroSectors.length === 1 ? 'it' : 'either one'} for what that zero does and does not mean.</p>`;

  const methodNote = document.createElement('div');
  methodNote.className = 'note legal caveat';
  methodNote.innerHTML = `<strong>How to read the ring.</strong>
    <p class="small">Rim ticks are coloured by what the court found, in the register's own words, and never
    by whether an outcome was good or bad for anyone — no finding is scored here. Corporation dots are
    sized by the <em>number</em> of determinations linked to them, an administrative fact about the
    corporation; nothing on this view is sized by, sorted by, or shaded by how much country a group holds.
    The inner ring is drawn in four closely-spaced lanes so that corporations sitting at nearly the same
    angle stay separately visible — the lane carries no meaning. Every position is fixed geometry computed
    from the two registers, so the diagram is identical on every load. A determination is a court's finding
    about a specific area at a specific time, and a determination is not automatically a recognition.</p>
    <p class="small">${esc(num(resolvedLinks))} links were read from the RNTBC areas register${
      unresolvedLinks ? `; ${esc(num(unresolvedLinks))} named a determination that is not on the determinations register and could not be drawn` : ''
    }.${unplaced.length ? ` ${esc(plural(unplaced.length, 'corporation is', 'corporations are'))} on the register with no determination linked at all, so ${unplaced.length === 1 ? 'it has' : 'they have'} no position on the ring — listed below.` : ''}</p>`;

  /* ────────────────────────── the A–Z list ─────────────────────────────── */

  const listCard = document.createElement('div');
  listCard.className = 'card stack-sm';
  const alpha = [...holders].sort((a, b) => a.name.localeCompare(b.name, 'en-AU'));

  listCard.innerHTML = `
    <div class="row">
      <div class="grow">
        <div class="card-title">Every corporation, A to Z</div>
        <div class="card-sub">Alphabetical — this list is not ordered by size, area or anything else.
          Names are reproduced exactly as the register states them.</div>
      </div>
    </div>
    <div class="control">
      <label class="field-label" for="holders-filter">Filter by name</label>
      <input type="search" id="holders-filter" placeholder="e.g. Aboriginal Corporation" autocomplete="off">
    </div>
    <div class="small muted" id="holders-count" aria-live="polite"></div>
    <div class="table-scroll">
      <table>
        <thead><tr>
          <th scope="col">Corporation</th>
          <th scope="col" class="num">Determinations</th>
          <th scope="col">Where</th>
          <th scope="col">Corporate register</th>
        </tr></thead>
        <tbody id="holders-tbody"></tbody>
      </table>
    </div>
    <div class="empty-state" id="holders-empty" hidden>No corporation on the register matches that text.</div>
  `;

  const tbody = listCard.querySelector<HTMLElement>('#holders-tbody')!;
  const rowsHtml = alpha.map((h) => {
    const c = corps.find((x) => x.holder === h);
    const own = c?.dets ?? [];
    const where = c ? c.jurisdictions.map(jurName).join(', ') : 'No determination linked';
    return `<tr data-name="${esc(h.name.toLowerCase())}">
      <td><button type="button" class="holders-name-btn verbatim" data-corp="${esc(c?.key ?? '')}"
        data-icn="${esc(h.icn ?? '')}" data-tip="${tipAttr(h.name, `Linked to ${plural(own.length, 'determination')}`, 'Open every determination it holds')}"
        >${esc(h.name)}</button></td>
      <td class="num">${esc(num(own.length))}</td>
      <td class="small">${esc(where)}</td>
      <td class="small">${h.register
        ? `<a href="${esc(h.register)}" target="_blank" rel="noopener">ORIC ↗</a>`
        : '<span class="muted">Not recorded</span>'}</td>
    </tr>`;
  }).join('');
  tbody.innerHTML = rowsHtml;

  const countEl = listCard.querySelector<HTMLElement>('#holders-count')!;
  const emptyEl = listCard.querySelector<HTMLElement>('#holders-empty')!;
  const filterEl = listCard.querySelector<HTMLInputElement>('#holders-filter')!;
  const allRows = [...tbody.querySelectorAll<HTMLTableRowElement>('tr')];

  const applyFilter = () => {
    const q = filterEl.value.trim().toLowerCase();
    let shown = 0;
    for (const row of allRows) {
      const on = !q || (row.dataset.name ?? '').includes(q);
      row.hidden = !on;
      if (on) shown += 1;
    }
    countEl.textContent = q
      ? `${num(shown)} of ${num(allRows.length)} corporations match “${filterEl.value.trim()}”.`
      : `${num(allRows.length)} corporations, ${num(resolvedLinks)} links.`;
    emptyEl.hidden = shown > 0;
  };
  filterEl.addEventListener('input', applyFilter);
  applyFilter();

  tbody.addEventListener('click', (e) => {
    const btn = (e.target as Element).closest<HTMLButtonElement>('.holders-name-btn');
    if (!btn) return;
    const key = btn.dataset.corp ?? '';
    const c = corps.find((x) => x.key === key);
    if (c) { openCorporation(c, corpsByDet); return; }
    // A corporation with no determination linked: still a real record, so the
    // click still opens something rather than doing nothing.
    const h = alpha.find((x) => (x.icn ?? '') === (btn.dataset.icn ?? '') && x.name === btn.textContent);
    if (h) openEmptyCorporation(h);
  });

  /* ─────────────────────────────── assemble ────────────────────────────── */

  wrap.append(controls, frame, legend, absence, crossNote, zeroNote, methodNote, listCard);
  host.append(wrap);

  // After the SVG is inside its frame, so the zoom controls have a parent.
  const zoom = attachSvgZoom(svg, { maxScale: 14 });
}

/* ───────────────────────────── overlay panels ────────────────────────────── */

function detCard(d: Determination): string {
  const finding = d.finding ? FINDING[d.finding] : null;
  const method = d.method ? METHOD[d.method] : null;
  return `<div class="holders-det">
    <div class="verbatim holders-det-name">${esc(d.name ?? d.id)}</div>
    <div class="detail-headline">
      <span class="pill" style="border-color:${finding?.fill ?? 'var(--border-default)'}">
        <span class="legend-swatch" style="background:${finding?.fill ?? 'transparent'}"></span>
        ${esc(finding?.label ?? d.findingText ?? 'Outcome not recorded')}
      </span>
      ${method ? `<span class="pill">${esc(method.label)}</span>` : ''}
      <span class="pill">${esc(jurName(d.juris))}</span>
      <span class="pill mono">${esc(longDate(d.date))}</span>
      ${d.sea ? '<span class="pill">Sea determination</span>' : ''}
    </div>
    ${d.holders ? `<div class="detail-field">
      <div class="detail-label">Native title holders, as the register states it</div>
      <div class="verbatim holders-text">${esc(d.holders)}</div>
    </div>` : '<p class="xsmall muted">The register records no holders text for this determination.</p>'}
    <div class="row small">
      <span class="mono xsmall muted">${esc(d.id)}</span>
      <span class="mono xsmall muted">${esc(km2(d.areaSqKm, { exact: true }))}</span>
      ${d.judgment ? `<a href="${esc(d.judgment)}" target="_blank" rel="noopener">Federal Court judgment ↗</a>` : '<span class="xsmall muted">No judgment link recorded</span>'}
      <button type="button" class="btn btn-sm" data-det-id="${esc(d.id)}">Open the full record</button>
    </div>
  </div>`;
}

function openCorporation(c: Corp, corpsByDet: Map<string, Corp[]>): void {
  openOverlay({
    panelClass: 'drawer',
    label: `Corporation: ${c.holder.name}`,
    lockScroll: true,
    build(panel, close) {
      const head = document.createElement('div');
      head.className = 'overlay-head';
      const h = document.createElement('h2');
      h.className = 'verbatim';
      h.textContent = c.holder.name;
      head.append(h, closeButton(close, 'Close this corporation'));

      const body = document.createElement('div');
      body.className = 'stack';
      const shared = c.dets.filter((d) => (corpsByDet.get(d.id) ?? []).length > 1).length;
      body.innerHTML = `
        <div class="detail-headline">
          <span class="pill">${esc(plural(c.dets.length, 'determination'))}</span>
          ${c.jurisdictions.map((j) => `<span class="pill">${esc(jurName(j))}</span>`).join('')}
          ${c.holder.icn ? `<span class="pill mono">ICN ${esc(c.holder.icn)}</span>` : ''}
        </div>
        <p class="small muted">A ${glossTerm('rntbc', 'registered native title body corporate')} holds or
          manages native title for the people the court recognised as holding it. It is the entity others
          must deal with about that country — it is not the holders, and this list is not a list of what it
          owns.</p>
        ${c.holder.register ? `<div class="detail-actions">
          <a class="btn" href="${esc(c.holder.register)}" target="_blank" rel="noopener">Public corporate register (ORIC) ↗</a>
        </div>` : '<p class="xsmall muted">The register records no corporate-register link for this corporation.</p>'}
        <div class="detail-block">
          <h3>Every determination linked to it</h3>
          <p class="xsmall muted">In date order. The holders text under each one is reproduced exactly as
            the National Native Title Register states it, in full.${shared
              ? ` ${esc(plural(shared, 'of these determinations is', 'of these determinations are'))} also linked to another corporation.`
              : ''}</p>
          <div class="stack">${c.dets.map(detCard).join('')}</div>
        </div>
        <div class="note legal xsmall">The link between a corporation and a determination is taken from the
          Tribunal's Registered Native Title Body Corporate areas register. The judgment, and the Tribunal's
          own registers, are the authoritative record. Nothing here is legal advice.</div>
      `;

      body.addEventListener('click', (e) => {
        const btn = (e.target as Element).closest<HTMLButtonElement>('[data-det-id]');
        if (!btn) return;
        const d = c.dets.find((x) => x.id === btn.dataset.detId);
        if (!d) return;
        // Close first: two stacked drawers share one Escape key, and the second
        // would hide the first entirely on a phone.
        close();
        openDetermination(d);
      });

      panel.append(head, body);
    },
  });
}

function openEmptyCorporation(h: Holder): void {
  openOverlay({
    panelClass: 'gloss-pop',
    label: `Corporation: ${h.name}`,
    build(panel, close) {
      const head = document.createElement('div');
      head.className = 'overlay-head';
      const t = document.createElement('h2');
      t.className = 'verbatim';
      t.textContent = h.name;
      head.append(t, closeButton(close, 'Close this corporation'));
      const body = document.createElement('div');
      body.className = 'stack-sm small';
      body.innerHTML = `
        <p>This corporation is on the Registered Native Title Body Corporate areas register, but the
          register links it to no determination. It therefore has no position on the ring.</p>
        <p class="muted">That is a fact about the register rather than about the corporation: the areas
          register and the determinations register are maintained separately and a link can be absent while
          the corporation itself is live.</p>
        ${h.icn ? `<p class="mono xsmall">ICN ${esc(h.icn)}</p>` : ''}
        ${h.register ? `<div class="detail-actions"><a class="btn" href="${esc(h.register)}" target="_blank" rel="noopener">Public corporate register (ORIC) ↗</a></div>` : ''}
      `;
      panel.append(head, body);
    },
  });
}

function openSector(
  s: Sector,
  corpsByDet: Map<string, Corp[]>,
  landKm2: number | null,
  totalDets: number,
): void {
  const corpKeys = new Set<string>();
  let unspoked = 0;
  let none = 0;
  for (const d of s.dets) {
    const held = corpsByDet.get(d.id) ?? [];
    if (!held.length) unspoked += 1;
    for (const c of held) corpKeys.add(c.key);
    if (d.finding === 'none') none += 1;
  }

  openOverlay({
    panelClass: 'gloss-pop',
    label: `${jurName(s.code)}: ${s.count} determinations`,
    build(panel, close) {
      const head = document.createElement('div');
      head.className = 'overlay-head';
      const t = document.createElement('h2');
      t.textContent = jurName(s.code);
      head.append(t, closeButton(close, `Close the summary for ${jurName(s.code)}`));

      const body = document.createElement('div');
      body.className = 'stack-sm small';
      body.innerHTML = s.count === 0
        ? `<p><strong>Zero determinations.</strong> Since 3 June 1992 the Federal Court has made
             ${esc(num(totalDets))} determinations under the Native Title Act. None of them is in
             ${esc(jurName(s.code))}.</p>
           <p class="muted">This is a real zero, not missing data. The register covers
             ${esc(jurName(s.code))}${landKm2 ? ` — ${esc(km2(landKm2))} of Australia's land` : ''} and
             records nothing there, so no ${glossTerm('rntbc', 'body corporate')} appears on the ring for
             it either. A zero on this register says nothing about whether Aboriginal and Torres Strait
             Islander people hold or have held that country; it records only that no
             ${glossTerm('determination', 'determination')} has been made about it under this Act.</p>`
        : `<p><strong>${esc(plural(s.count, 'determination'))}</strong>, held between
             ${esc(num(corpKeys.size))} ${corpKeys.size === 1 ? 'corporation' : 'corporations'}.</p>
           <ul class="plain-list">
             <li><span class="grow">First determination</span>
               <span class="mono">${esc(longDate(s.dets[0]?.date))}</span></li>
             <li><span class="grow">Most recent</span>
               <span class="mono">${esc(longDate(s.dets[s.dets.length - 1]?.date))}</span></li>
             <li><span class="grow">Found native title does not exist</span>
               <span class="mono">${esc(num(none))}</span></li>
             <li><span class="grow">With no corporation linked</span>
               <span class="mono">${esc(num(unspoked))}</span></li>
           </ul>
           <p class="muted">Ticks in this sector run clockwise in date order, oldest first. The sector's
             arc is proportional to the number of determinations, not to area of country.</p>`;
      panel.append(head, body);
    },
  });
}
